import type { AdminAuditEntry, AdminChannelDetail, AdminChannelRow, AdminRole, AdminSummary, AdminSystemInfo, AdminUserDetail, AdminUserRow, Attachment, Channel, ChannelMember, ChannelRole, Message, MessageDetail, PageResult, Project, Reminder, Schedule, ScheduleCreatePayload, SearchPayload, SyncSnapshot, TodayPayload, Todo, User, UserSettingsPatch } from "./types";

const API_BASE_KEY = "mindback.apiBase";
const TOKEN_KEY = "mindback.token";
const PRODUCTION_API_BASE = "https://moonify.cn";

function trimApiBase(value: string): string {
  return value.replace(/\/$/, "");
}

function isLocalHostname(hostname: string): boolean {
  return hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
}

function browserOrigin(): string | null {
  if (typeof window === "undefined") return null;
  if (window.location.protocol !== "http:" && window.location.protocol !== "https:") return null;
  return window.location.origin;
}

function resolveDefaultApiBase(): string {
  const configured = import.meta.env.VITE_API_BASE_URL?.trim();
  const origin = browserOrigin();
  if (configured) {
    const apiBase = trimApiBase(configured);
    if (origin && window.location.protocol === "https:" && apiBase.startsWith("http://") && !isLocalHostname(window.location.hostname)) {
      return origin;
    }
    return apiBase;
  }
  if (origin && !isLocalHostname(window.location.hostname)) return origin;
  return PRODUCTION_API_BASE;
}

const DEFAULT_API_BASE = resolveDefaultApiBase();

export function getApiBase(): string {
  localStorage.removeItem(API_BASE_KEY);
  return DEFAULT_API_BASE;
}

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string | null) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
  else localStorage.removeItem(TOKEN_KEY);
}

export type AttachmentVariant = "original" | "thumbnail";

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = getToken();
  const formData = options.body instanceof FormData;
  const headers: Record<string, string> = { ...((options.headers || {}) as Record<string, string>) };
  if (!formData) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  const response = await fetch(`${getApiBase()}${path}`, { ...options, headers });
  if (!response.ok) {
    let message = response.statusText;
    try {
      const data = await response.json();
      message = data.message || data.detail || message;
    } catch {
      // keep status text
    }
    throw new Error(message);
  }
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) return response.json() as Promise<T>;
  return response.text() as Promise<T>;
}

export const api = {
  register(email: string, password: string): Promise<{ token: string; user: User }> {
    return request("/api/auth/register", { method: "POST", body: JSON.stringify({ email, password }) });
  },
  login(email: string, password: string): Promise<{ token: string; user: User }> {
    return request("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
  },
  me(): Promise<{ user: User }> {
    return request("/api/auth/me");
  },
  settings(payload: UserSettingsPatch): Promise<{ user: User }> {
    return request("/api/settings", { method: "PATCH", body: JSON.stringify(payload) });
  },
  sync(params: { since?: string; channels?: string[] } = {}): Promise<SyncSnapshot> {
    const query = new URLSearchParams();
    if (params.since) query.set("since", params.since);
    if (params.channels?.length) query.set("channels", params.channels.join(","));
    return request(`/api/sync${query.toString() ? `?${query}` : ""}`);
  },
  channels(): Promise<{ channels: Channel[] }> {
    return request("/api/channels");
  },
  createChannel(payload: { name: string; slug?: string; summary?: string; ai_enabled?: boolean }): Promise<{ channel: Channel }> {
    return request("/api/channels", { method: "POST", body: JSON.stringify(payload) });
  },
  patchChannel(id: string, payload: Partial<Channel>): Promise<{ channel: Channel }> {
    return request(`/api/channels/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  deleteChannel(id: string): Promise<{ ok: boolean }> {
    return request(`/api/channels/${id}`, { method: "DELETE" });
  },
  channelMembers(id: string): Promise<{ members: ChannelMember[] }> {
    return request(`/api/channels/${id}/members`);
  },
  inviteMember(id: string, payload: { email: string; role: ChannelRole }): Promise<{ member: ChannelMember }> {
    return request(`/api/channels/${id}/members`, { method: "POST", body: JSON.stringify(payload) });
  },
  patchMember(channelId: string, userId: string, payload: { role: ChannelRole }): Promise<{ members: ChannelMember[] }> {
    return request(`/api/channels/${channelId}/members/${userId}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  removeMember(channelId: string, userId: string): Promise<{ ok: boolean }> {
    return request(`/api/channels/${channelId}/members/${userId}`, { method: "DELETE" });
  },
  markChannelRead(id: string): Promise<{ channel: Channel }> {
    return request(`/api/channels/${id}/read`, { method: "POST" });
  },
  muteChannel(id: string, muted: boolean): Promise<{ channel: Channel }> {
    return request(`/api/channels/${id}/mute`, { method: "POST", body: JSON.stringify({ muted }) });
  },
  upload(file: File): Promise<{ attachment: Attachment }> {
    const body = new FormData();
    body.append("file", file);
    return request("/api/uploads", { method: "POST", body });
  },
  async attachmentBlob(id: string, variant: AttachmentVariant = "original", signal?: AbortSignal): Promise<Blob> {
    const token = getToken();
    const query = variant === "original" ? "" : `?variant=${variant}`;
    const response = await fetch(`${getApiBase()}/api/uploads/${id}${query}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal
    });
    if (!response.ok) throw new Error(response.statusText);
    return response.blob();
  },
  createMessage(content: string, skip_ai = false, channel_id?: string, attachment_ids: string[] = []): Promise<{ message: Message; derived: { todos: Todo[]; reminders: Reminder[]; schedules?: Schedule[] }; attachments: Attachment[] }> {
    return request("/api/messages", { method: "POST", body: JSON.stringify({ content, skip_ai, channel_id, attachment_ids }) });
  },
  messageDetail(id: string): Promise<MessageDetail> {
    return request(`/api/messages/${id}`);
  },
  patchMessage(id: string, payload: Partial<Message>): Promise<{ message: Message }> {
    return request(`/api/messages/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  deleteMessage(id: string): Promise<{ ok: boolean; effect: string }> {
    return request(`/api/messages/${id}`, { method: "DELETE" });
  },
  confirmMessage(id: string, payload: { action: string; title?: string; due_at?: string; priority?: Todo["priority"]; remind_at?: string; end_at?: string; all_day?: boolean; remind_minutes_before?: number; alarm_enabled?: boolean; project_id?: string; project_name?: string }): Promise<unknown> {
    return request(`/api/messages/${id}/confirm`, { method: "POST", body: JSON.stringify(payload) });
  },
  today(channel?: string): Promise<TodayPayload> {
    return request(`/api/today${channel ? `?channel=${encodeURIComponent(channel)}` : ""}`);
  },
  patchTodo(id: string, payload: Partial<Todo>): Promise<{ todo: Todo }> {
    return request(`/api/todos/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  createTodo(payload: { title: string; due_at?: string; message_id?: string; priority?: Todo["priority"] }): Promise<{ todo: Todo }> {
    return request("/api/todos", { method: "POST", body: JSON.stringify(payload) });
  },
  createReminder(payload: { title: string; remind_at: string; message_id?: string; kind?: string }): Promise<{ reminder: Reminder }> {
    return request("/api/reminders", { method: "POST", body: JSON.stringify(payload) });
  },
  patchReminder(id: string, payload: Partial<Reminder> & { snooze_minutes?: number }): Promise<{ reminder: Reminder }> {
    return request(`/api/reminders/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  schedules(params: { from?: string; to?: string; status?: string; channel?: string } = {}): Promise<{ schedules: Schedule[] }> {
    const query = new URLSearchParams();
    if (params.from) query.set("from", params.from);
    if (params.to) query.set("to", params.to);
    if (params.status) query.set("status", params.status);
    if (params.channel) query.set("channel", params.channel);
    return request(`/api/schedules${query.toString() ? `?${query}` : ""}`);
  },
  createSchedule(payload: ScheduleCreatePayload): Promise<{ schedule: Schedule }> {
    return request("/api/schedules", { method: "POST", body: JSON.stringify(payload) });
  },
  patchSchedule(id: string, payload: Partial<Schedule>): Promise<{ schedule: Schedule }> {
    return request(`/api/schedules/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  deleteSchedule(id: string): Promise<{ ok: boolean; effect: string }> {
    return request(`/api/schedules/${id}`, { method: "DELETE" });
  },
  search(params: {
    q?: string;
    channel?: string;
    from_date?: string;
    to_date?: string;
    types?: string[];
    attachment_type?: string;
    project_id?: string;
  } = {}): Promise<SearchPayload> {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.channel) query.set("channel", params.channel);
    if (params.from_date) query.set("from_date", params.from_date);
    if (params.to_date) query.set("to_date", params.to_date);
    if (params.types?.length) query.set("types", params.types.join(","));
    if (params.attachment_type) query.set("attachment_type", params.attachment_type);
    if (params.project_id) query.set("project_id", params.project_id);
    return request(`/api/search${query.toString() ? `?${query}` : ""}`);
  },
  createProject(name: string, summary = "", channel_id?: string): Promise<{ project: Project }> {
    return request("/api/projects", { method: "POST", body: JSON.stringify({ name, summary, channel_id }) });
  },
  project(id: string): Promise<{ project: Project; messages: Message[] }> {
    return request(`/api/projects/${id}`);
  },
  patchProject(id: string, payload: Partial<Project>): Promise<{ project: Project }> {
    return request(`/api/projects/${id}`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  deleteProject(id: string): Promise<{ ok: boolean; effect: string }> {
    return request(`/api/projects/${id}`, { method: "DELETE" });
  },
  addProjectMessage(projectId: string, messageId: string): Promise<{ ok: boolean }> {
    return request(`/api/projects/${projectId}/messages`, { method: "POST", body: JSON.stringify({ message_id: messageId }) });
  },
  removeProjectMessage(projectId: string, messageId: string): Promise<{ ok: boolean; effect: string }> {
    return request(`/api/projects/${projectId}/messages/${messageId}`, { method: "DELETE" });
  },
  exportData(format: "json" | "md" | "csv"): Promise<unknown> {
    return request(`/api/export?format=${format}`);
  },
  deleteAccountData(): Promise<{ ok: boolean; effect: string }> {
    return request("/api/account/data", { method: "DELETE" });
  },
  adminSummary(): Promise<AdminSummary> {
    return request("/api/admin/summary");
  },
  adminUsers(params: { q?: string; status?: string; role?: AdminRole | ""; page?: number; page_size?: number } = {}): Promise<PageResult<AdminUserRow>> {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.status) query.set("status", params.status);
    if (params.role) query.set("role", params.role);
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    return request(`/api/admin/users${query.toString() ? `?${query}` : ""}`);
  },
  adminUser(id: string): Promise<AdminUserDetail> {
    return request(`/api/admin/users/${id}`);
  },
  adminUserStatus(id: string, payload: { status: "active" | "disabled"; reason?: string }): Promise<AdminUserDetail> {
    return request(`/api/admin/users/${id}/status`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  adminResetUserPassword(id: string, payload: { password: string; reason: string }): Promise<AdminUserDetail> {
    return request(`/api/admin/users/${id}/password`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  adminClearUserData(id: string, payload: { confirm_email: string; reason: string }): Promise<AdminUserDetail> {
    return request(`/api/admin/users/${id}/data`, { method: "DELETE", body: JSON.stringify(payload) });
  },
  adminUserRole(id: string, payload: { admin_role: AdminRole; reason?: string }): Promise<AdminUserDetail> {
    return request(`/api/admin/users/${id}/admin-role`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  adminChannels(params: { q?: string; status?: string; page?: number; page_size?: number } = {}): Promise<PageResult<AdminChannelRow>> {
    const query = new URLSearchParams();
    if (params.q) query.set("q", params.q);
    if (params.status) query.set("status", params.status);
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    return request(`/api/admin/channels${query.toString() ? `?${query}` : ""}`);
  },
  adminChannel(id: string): Promise<AdminChannelDetail> {
    return request(`/api/admin/channels/${id}`);
  },
  adminChannelStatus(id: string, payload: { status: "active" | "archived"; reason?: string }): Promise<AdminChannelDetail> {
    return request(`/api/admin/channels/${id}/status`, { method: "PATCH", body: JSON.stringify(payload) });
  },
  adminAudit(params: { target_type?: string; target_id?: string; page?: number; page_size?: number } = {}): Promise<PageResult<AdminAuditEntry>> {
    const query = new URLSearchParams();
    if (params.target_type) query.set("target_type", params.target_type);
    if (params.target_id) query.set("target_id", params.target_id);
    if (params.page) query.set("page", String(params.page));
    if (params.page_size) query.set("page_size", String(params.page_size));
    return request(`/api/admin/audit${query.toString() ? `?${query}` : ""}`);
  },
  adminSystem(): Promise<AdminSystemInfo> {
    return request("/api/admin/system");
  },
  adminRegistration(payload: { registration_enabled: boolean; reason: string }): Promise<AdminSystemInfo> {
    return request("/api/admin/system/registration", { method: "PATCH", body: JSON.stringify(payload) });
  }
};
